import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, Sparkles, Loader2, BellRing, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-suggests up to 3 nearest bookable slots when the anchor date/time
 * has no availability. Same day first, then walks forward day-by-day
 * (Karine spec Feb 2026 — "closest is 12:00 PM one tap away instead of
 * blank no times").
 *
 * Bonus (Sep 22 2026 · Karine ask): when the nearest opening is 3+ days
 * out from the anchor date, offer a Live Waitlist Nudge in the SAME card
 * so the client can also hold the original date via `POST /api/waitlist/signup`.
 *
 * Props:
 *   locationId, date (YYYY-MM-DD anchor), durationMinutes, provider,
 *   variationId (optional), preferredTime (optional "HH:MM"),
 *   maxDays (default 3), limit (default 3), isFr (bool),
 *   onPick({date, day_name, start_time}) → parent applies the selection.
 *   waitlist: {
 *     treatmentName,       // service label — falls back to "requested treatment"
 *     locationName,        // "Ottawa" / "Kanata" / "Carleton Place"
 *     client: { name?, email?, phone? },  // pre-filled if known
 *     threshold: 3         // days_from_anchor threshold; default 3
 *   }
 */
function fmtTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = ((hour + 11) % 12) + 1;
  return `${h12}:${m} ${ampm}`;
}

function fmtDayLabel(dateStr, dayName, isFr) {
  try {
    const today = new Date();
    const y = today.getFullYear();
    const mo = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const todayStr = `${y}-${mo}-${day}`;
    const tmr = new Date(today); tmr.setDate(today.getDate() + 1);
    const tmrStr = `${tmr.getFullYear()}-${String(tmr.getMonth() + 1).padStart(2, "0")}-${String(tmr.getDate()).padStart(2, "0")}`;
    if (dateStr === todayStr) return isFr ? "Aujourd'hui" : "Today";
    if (dateStr === tmrStr) return isFr ? "Demain" : "Tomorrow";
    // Parse YYYY-MM-DD without timezone drift
    const [Y, M, D] = dateStr.split("-").map((n) => parseInt(n, 10));
    const d = new Date(Y, M - 1, D);
    const wk = d.toLocaleDateString(isFr ? "fr-CA" : "en-US", { weekday: "short" });
    const md = d.toLocaleDateString(isFr ? "fr-CA" : "en-US", { month: "short", day: "numeric" });
    return `${wk} ${md}`;
  } catch {
    return dayName || dateStr;
  }
}

export default function NearestSlotsCard({
  locationId,
  date,
  durationMinutes = 30,
  provider = "karine",
  variationId = null,
  preferredTime = null,
  // Oct 2 2026 · Karine P0 — bumped 3→14 after site-down ticket.
  // With max_days=3 and an anchor on a Sat (Karine OFF), the search
  // window hits Sat+Sun+Mon → Monday is the only chance and gets
  // mis-reported as 0 if Square's Mon cache was still warming on
  // the first request. 14 days always reaches at least one Mon/Fri
  // AND Linda's Thursday, so the fallback card is NEVER empty.
  maxDays = 14,
  limit = 3,
  isFr = false,
  onPick,
  compact = false,
  waitlist = null,
}) {
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [errored, setErrored] = useState(false);
  // Live Waitlist Nudge state
  const [wlOpen, setWlOpen] = useState(false);
  const [wlSubmitting, setWlSubmitting] = useState(false);
  const [wlDone, setWlDone] = useState(false);
  const [wlForm, setWlForm] = useState({
    name: (waitlist && waitlist.client && waitlist.client.name) || "",
    email: (waitlist && waitlist.client && waitlist.client.email) || "",
    phone: (waitlist && waitlist.client && waitlist.client.phone) || "",
  });

  useEffect(() => {
    let cancelled = false;
    if (!locationId || !date) return undefined;
    setLoading(true);
    setErrored(false);
    axios
      .post(
        `${API}/api/square/nearest-slots`,
        {
          location_id: locationId,
          date,
          service_duration_minutes: durationMinutes,
          provider: (provider || "karine").toLowerCase(),
          service_variation_id: variationId || null,
          preferred_time: preferredTime || null,
          max_days: maxDays,
          limit,
        },
        { timeout: 45000 },
      )
      .then((res) => {
        if (cancelled) return;
        setSlots((res.data && res.data.slots) || []);
      })
      .catch(() => {
        if (cancelled) return;
        setErrored(true);
        setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, date, durationMinutes, provider, variationId, preferredTime, maxDays, limit]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center gap-2 py-4 text-xs text-zinc-400"
        data-testid="nearest-slots-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
        {isFr ? "Recherche du prochain créneau…" : "Finding the closest opening…"}
      </div>
    );
  }

  if (errored || slots.length === 0) return null;

  // ── Live Waitlist Nudge trigger ────────────────────────────────────
  // When the nearest slot is >= threshold days out from the anchor date
  // we surface a compact "Prefer the original date? Join the waitlist"
  // strip inside the same card. Falls back gracefully when parent didn't
  // pass a `waitlist` prop.
  const wlThreshold = (waitlist && waitlist.threshold) || 3;
  const wlEligible = !!waitlist
    && slots.length > 0
    && Math.min(...slots.map((s) => s.days_from_anchor || 0)) >= wlThreshold;

  const submitWaitlist = async () => {
    if (wlSubmitting || wlDone) return;
    const name = (wlForm.name || "").trim();
    const email = (wlForm.email || "").trim();
    if (!name || !email) {
      toast.error(isFr ? "Nom + courriel requis" : "Name + email required");
      return;
    }
    setWlSubmitting(true);
    try {
      await axios.post(`${API}/api/waitlist/signup`, {
        name,
        email,
        phone: (wlForm.phone || "").trim(),
        preferred_location: (waitlist && waitlist.locationName) || "",
        flexible_location: false,
        treatments_wanted: [(waitlist && waitlist.treatmentName) || "Requested treatment"],
        preferred_day: date || "",
        preferred_time: preferredTime || "",
        flexible_schedule: false,
        notes: `Auto-added from nearest-slot suggestion · nearest opening is ${slots[0]?.date} ${slots[0]?.start_time}`,
      }, { timeout: 15000 });
      setWlDone(true);
      toast.success(isFr
        ? "Ajouté à la liste d'attente — nous vous appelons dès qu'un créneau s'ouvre."
        : "Added to the waitlist — we'll text you the moment a slot opens up.");
    } catch (err) {
      toast.error(isFr ? "Impossible d'ajouter — réessayez" : "Could not add — please try again");
    } finally {
      setWlSubmitting(false);
    }
  };

  return (
    <div
      className={`rounded-xl border-2 border-amber-400/60 bg-gradient-to-br from-amber-500/15 via-amber-400/5 to-transparent ${compact ? "p-3" : "p-4"} shadow-lg shadow-amber-500/10`}
      data-testid="nearest-slots-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <p className="text-[10px] sm:text-xs uppercase tracking-[2px] font-black text-amber-300">
          {(() => {
            // Feb 2026 · Karine panic — when the fallback finds slots
            // on the SAME anchor date we picked, don't label it
            // "Closest available" (sounds like we're giving up); label
            // it "Available times" so clients confidently tap.
            const allAnchor = slots.every((s) => s.date === date);
            if (allAnchor) return isFr ? "Créneaux disponibles" : "Available times";
            return isFr ? "Créneaux les plus proches" : "Closest available";
          })()}
        </p>
      </div>
      <p className="text-zinc-300 text-xs mb-3 leading-snug">
        {(() => {
          const allAnchor = slots.every((s) => s.date === date);
          if (preferredTime && !allAnchor) {
            return isFr
              ? `Rien à ${fmtTime12(preferredTime)} — voici ce qui est le plus proche :`
              : `Nothing at ${fmtTime12(preferredTime)} — here's what's closest:`;
          }
          if (allAnchor) {
            return isFr ? "Choisissez votre heure préférée :" : "Pick your preferred time:";
          }
          return isFr
            ? "Aucun créneau ce jour-là — voici les prochains disponibles :"
            : "No openings that day — here are the next ones we have:";
        })()}
      </p>
      <div className="grid gap-2">
        {slots.map((s) => (
          <button
            key={`${s.date}-${s.start_time}`}
            type="button"
            onClick={() => onPick && onPick(s)}
            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-zinc-900/60 hover:bg-amber-500/20 border border-amber-400/40 hover:border-amber-400 transition-all group"
            data-testid={`nearest-slot-${s.date}-${s.start_time}`}
          >
            <div className="text-left">
              <p className="text-white text-sm font-bold tabular-nums flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-amber-300" />
                {fmtDayLabel(s.date, s.day_name, isFr)} · {fmtTime12(s.start_time)}
              </p>
              {s.best_fit && (
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-300 mt-0.5">
                  ⚡ {isFr ? "Meilleur ajustement" : "Best fit"}
                </p>
              )}
            </div>
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-zinc-900 bg-amber-400 group-hover:bg-amber-300 rounded-full px-2.5 py-1 whitespace-nowrap">
              {isFr ? "Choisir →" : "Book →"}
            </span>
          </button>
        ))}
      </div>

      {/* ── Live Waitlist Nudge (Sep 22 2026 · Karine) ────────────
          When the closest opening is 3+ days out we let clients also
          hold their original date via the waitlist in the same card. */}
      {wlEligible && (
        <div
          className="mt-3 pt-3 border-t border-amber-400/30"
          data-testid="waitlist-nudge"
        >
          {wlDone ? (
            <p className="flex items-center gap-2 text-xs text-emerald-300 font-semibold">
              <Check className="w-3.5 h-3.5" />
              {isFr
                ? `Vous êtes sur la liste d'attente pour le ${date}. Nous vous avertirons dès qu'un créneau s'ouvre.`
                : `You're on the waitlist for ${date}. We'll text you the moment a slot opens.`}
            </p>
          ) : !wlOpen ? (
            <button
              type="button"
              onClick={() => {
                // If we already have client info (portal/reschedule flows),
                // 1-tap submit. Otherwise expand the mini form.
                if ((wlForm.name || "").trim() && (wlForm.email || "").trim()) {
                  submitWaitlist();
                } else {
                  setWlOpen(true);
                }
              }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-400/50 hover:border-emerald-400 transition-all group"
              data-testid="waitlist-nudge-open"
            >
              <div className="text-left">
                <p className="text-emerald-200 text-sm font-bold flex items-center gap-2">
                  <BellRing className="w-3.5 h-3.5" />
                  {isFr
                    ? `Vous préférez le ${date} ?`
                    : `Prefer ${date} instead?`}
                </p>
                <p className="text-emerald-300/80 text-[11px] mt-0.5">
                  {isFr
                    ? "Rejoignez la liste d'attente — nous vous avertirons dès qu'un créneau s'ouvre ce jour-là."
                    : "Join the waitlist — we'll text you the second a slot opens that day."}
                </p>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-900 bg-emerald-300 group-hover:bg-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                {isFr ? "M'ajouter" : "Notify me"}
              </span>
            </button>
          ) : (
            <div className="space-y-2" data-testid="waitlist-nudge-form">
              <p className="text-emerald-200 text-xs font-semibold flex items-center gap-2">
                <BellRing className="w-3.5 h-3.5" />
                {isFr
                  ? `Ajoutez-vous à la liste d'attente pour le ${date}`
                  : `Add yourself to the waitlist for ${date}`}
              </p>
              <input
                type="text"
                value={wlForm.name}
                onChange={(e) => setWlForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={isFr ? "Nom complet" : "Full name"}
                className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900/70 border border-zinc-700 text-white text-xs focus:border-emerald-400 focus:outline-none"
                data-testid="waitlist-nudge-name"
              />
              <input
                type="email"
                value={wlForm.email}
                onChange={(e) => setWlForm((f) => ({ ...f, email: e.target.value }))}
                placeholder={isFr ? "Courriel" : "Email"}
                className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900/70 border border-zinc-700 text-white text-xs focus:border-emerald-400 focus:outline-none"
                data-testid="waitlist-nudge-email"
              />
              <input
                type="tel"
                value={wlForm.phone}
                onChange={(e) => setWlForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder={isFr ? "Téléphone (facultatif)" : "Phone (optional)"}
                className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900/70 border border-zinc-700 text-white text-xs focus:border-emerald-400 focus:outline-none"
                data-testid="waitlist-nudge-phone"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={submitWaitlist}
                  disabled={wlSubmitting}
                  className="flex-1 px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-zinc-900 text-xs font-black uppercase tracking-widest transition"
                  data-testid="waitlist-nudge-submit"
                >
                  {wlSubmitting
                    ? (isFr ? "Envoi…" : "Adding…")
                    : (isFr ? "Rejoindre la liste" : "Join waitlist")}
                </button>
                <button
                  type="button"
                  onClick={() => setWlOpen(false)}
                  className="px-3 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium"
                  data-testid="waitlist-nudge-cancel"
                >
                  {isFr ? "Annuler" : "Cancel"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
